export type AppEnv = 'production' | 'test' | 'development';
export type RuntimeTarget = 'production' | 'deployed-test' | 'local-emulator';
export type TestModeSource = 'app-env' | 'legacy-query' | 'none';

export interface RuntimeEnvInput {
  mode: string;
  dev: boolean;
  prod: boolean;
  appEnv: string | undefined;
  runtimeTarget: string | undefined;
  useFirebaseEmulators: string | undefined;
  firebaseProjectId: string | undefined;
  search: string;
}

export interface RuntimeEnv {
  appEnv: AppEnv;
  runtimeTarget: RuntimeTarget;
  isProduction: boolean;
  isDevelopment: boolean;
  isTest: boolean;
  isNonProduction: boolean;
  usesFirebaseEmulators: boolean;
  allowManualTestTools: boolean;
  allowLocalAdminBypass: boolean;
  legacyQueryRequestedTestMode: boolean;
  testModeSource: TestModeSource;
  firebaseProjectId: string | undefined;
  configurationErrors: string[];
}

const APP_ENVS: AppEnv[] = ['production', 'test', 'development'];
const RUNTIME_TARGETS: RuntimeTarget[] = ['production', 'deployed-test', 'local-emulator'];

function normalizeAppEnv(value: string | undefined, dev: boolean): AppEnv {
  if (value && APP_ENVS.includes(value as AppEnv)) {
    return value as AppEnv;
  }
  return dev ? 'development' : 'production';
}

function normalizeRuntimeTarget(
  value: string | undefined,
  appEnv: AppEnv,
  usesFirebaseEmulators: boolean,
): RuntimeTarget {
  if (value && RUNTIME_TARGETS.includes(value as RuntimeTarget)) {
    return value as RuntimeTarget;
  }
  if (usesFirebaseEmulators) {
    return 'local-emulator';
  }
  return appEnv === 'test' ? 'deployed-test' : 'production';
}

function looksProductionProject(projectId: string | undefined): boolean {
  return Boolean(projectId && /(^|[-_])prod(uction)?($|[-_])/i.test(projectId));
}

export function isLegacyTestModeRequest(search: string): boolean {
  return new URLSearchParams(search).get('testMode') === 'true';
}

export function createRuntimeEnv(input: RuntimeEnvInput): RuntimeEnv {
  const appEnv = normalizeAppEnv(input.appEnv, input.dev);
  const legacyQueryRequestedTestMode = isLegacyTestModeRequest(input.search);
  const usesFirebaseEmulators = input.useFirebaseEmulators === 'true';
  const runtimeTarget = normalizeRuntimeTarget(
    input.runtimeTarget,
    appEnv,
    usesFirebaseEmulators,
  );

  const testModeSource: TestModeSource =
    appEnv === 'test'
      ? 'app-env'
      : appEnv !== 'production' && legacyQueryRequestedTestMode
        ? 'legacy-query'
        : 'none';

  const isTest = testModeSource !== 'none';
  const configurationErrors: string[] = [];

  if (appEnv === 'production' && usesFirebaseEmulators) {
    configurationErrors.push('Production runtime cannot use Firebase emulators.');
  }

  if (appEnv === 'production' && runtimeTarget !== 'production') {
    configurationErrors.push('Production runtime target must be production.');
  }

  if (appEnv === 'test' && runtimeTarget === 'production') {
    configurationErrors.push('Test runtime target cannot be production.');
  }

  if (
    appEnv === 'test' &&
    runtimeTarget === 'deployed-test' &&
    looksProductionProject(input.firebaseProjectId)
  ) {
    configurationErrors.push('Deployed test runtime cannot use a production-looking Firebase project.');
  }

  return {
    appEnv,
    runtimeTarget,
    isProduction: appEnv === 'production',
    isDevelopment: appEnv === 'development',
    isTest,
    isNonProduction: appEnv !== 'production',
    usesFirebaseEmulators,
    allowManualTestTools: appEnv === 'test',
    allowLocalAdminBypass: appEnv !== 'production',
    legacyQueryRequestedTestMode,
    testModeSource,
    firebaseProjectId: input.firebaseProjectId,
    configurationErrors,
  };
}

export const runtimeEnv = createRuntimeEnv({
  mode: import.meta.env.MODE,
  dev: import.meta.env.DEV,
  prod: import.meta.env.PROD,
  appEnv: import.meta.env.VITE_APP_ENV,
  runtimeTarget: import.meta.env.VITE_RUNTIME_TARGET,
  useFirebaseEmulators: import.meta.env.VITE_USE_FIREBASE_EMULATORS,
  firebaseProjectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  search: typeof window === 'undefined' ? '' : window.location.search,
});

export function formatRuntimeGameNumber(gameNumber: number | undefined): string {
  const padded = String(gameNumber ?? 0).padStart(3, '0');
  if (!runtimeEnv.isNonProduction) {
    return padded;
  }
  const prefix = runtimeEnv.appEnv === 'test' ? 'TEST' : 'DEV';
  return `${prefix}-${padded}`;
}
